import { PrivateAxiosInstance } from '@/api/axios'
import type { AxiosInstance } from 'axios'

/**
 * Parse a block of Server-Sent Events text and invoke the callback for each
 * complete `event:`/`data:` frame.
 */
export const parseSseChunk = (chunk: string, onEvent: (event: string, data: string) => void) => {
  const blocks = chunk.split('\n\n')
  for (const block of blocks) {
    if (!block.trim()) continue
    let event = 'message'
    const dataLines: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (dataLines.length > 0) onEvent(event, dataLines.join('\n'))
  }
}

/**
 * POST to an authenticated SSE endpoint and forward each event to the callback
 * as it arrives. Resolves when the stream ends. No timeout is applied - the
 * server keeps the connection alive while it streams.
 */
/**
 * Consume an AI generation SSE stream (`text`/`done`/`error` events). Calls
 * `onContent` with the accumulated content (throttled) while streaming and
 * returns the complete content when the stream ends. Throws on stream errors.
 */
export const streamAiGeneration = async ({
  endpoint,
  body,
  onContent,
  throttleMs = 150,
  signal,
  axiosInstance,
}: {
  endpoint: string
  body: unknown
  onContent: (content: string) => void
  throttleMs?: number
  signal?: AbortSignal
  /** Defaults to the authenticated instance; pass PublicAxiosInstance for token-link pages */
  axiosInstance?: AxiosInstance
}): Promise<string> => {
  let generated = ''
  let finalContent: string | null = null
  let streamError: string | null = null
  let lastFlush = 0

  await postSseStream({
    endpoint,
    body,
    signal,
    axiosInstance,
    onEvent: (event, payload) => {
      if (event === 'text') {
        try {
          generated += (JSON.parse(payload) as { text: string }).text
        } catch {
          return
        }
        // Throttle updates so the consumer (e.g. a rich text editor) keeps up
        const now = Date.now()
        if (now - lastFlush > throttleMs) {
          lastFlush = now
          onContent(generated)
        }
      } else if (event === 'done') {
        try {
          finalContent = (JSON.parse(payload) as { content: string }).content
        } catch {
          finalContent = generated
        }
      } else if (event === 'error') {
        try {
          streamError = (JSON.parse(payload) as { message: string }).message
        } catch {
          streamError = 'Failed to generate content'
        }
      }
    },
  })

  if (streamError) throw new Error(streamError)

  const content = finalContent ?? generated
  if (!content) throw new Error('No content was generated')

  onContent(content)
  return content
}

export const postSseStream = async ({
  endpoint,
  body,
  onEvent,
  signal,
  axiosInstance = PrivateAxiosInstance,
}: {
  endpoint: string
  body: unknown
  onEvent: (event: string, data: string) => void
  signal?: AbortSignal
  axiosInstance?: AxiosInstance
}): Promise<void> => {
  const response = await axiosInstance.post(endpoint, body, {
    signal,
    headers: {
      Accept: 'text/event-stream',
    },
    responseType: 'stream',
    adapter: 'fetch',
    timeout: 0,
  })

  if (response.status < 200 || response.status >= 300 || !response.data)
    throw new Error(`Request failed: ${response.status}`)

  const reader = (response.data as ReadableStream<Uint8Array>).getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const throwIfAborted = () => {
    if (!signal?.aborted) return
    const error = new Error('The operation was aborted')
    error.name = 'AbortError'
    throw error
  }

  const onAbort = () => {
    void reader.cancel().catch(() => undefined)
  }
  signal?.addEventListener('abort', onAbort)

  try {
    throwIfAborted()
    while (true) {
      const { value, done } = await reader.read()
      throwIfAborted()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lastSep = buffer.lastIndexOf('\n\n')
      if (lastSep === -1) continue
      const ready = buffer.slice(0, lastSep + 2)
      buffer = buffer.slice(lastSep + 2)
      parseSseChunk(ready, onEvent)
    }
  } finally {
    signal?.removeEventListener('abort', onAbort)
  }
}
